import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import StepByStepGuide from '../../components/StepByStepGuide';
import DiscordWidget from '../../components/DiscordWidget';
import '../Dashboard.css';
import './Module.css';

const MODULE_ID = 4;

// Quiz sur l'automatisation
const quizQuestions = [
  {
    question: "Quelle est la règle des 3R pour identifier une tâche automatisable ?",
    options: [
      "Rapide, Rentable, Réalisable",
      "Répétitive, Régulière, Robotisable",
      "Robuste, Reliable, Reproductible",
      "Réduite, Rationalisée, Réorganisée"
    ],
    correctAnswer: 1,
    explanation: "La règle des 3R : une tâche est automatisable si elle est Répétitive, Régulière et Robotisable (ne nécessite pas de jugement humain complexe)."
  },
  {
    question: "Quel outil permet de connecter +5000 applications entre elles sans code ?",
    options: [
      "Make (Integromat)",
      "GitHub Actions",
      "Zapier",
      "IFTTT uniquement"
    ],
    correctAnswer: 2,
    explanation: "Zapier est le 'couteau suisse' de l'automatisation avec plus de 5000 intégrations possibles sans écrire de code."
  },
  {
    question: "Quelle est la première étape pour créer une automatisation efficace ?",
    options: [
      "Choisir l'outil d'automatisation",
      "Documenter le processus actuel",
      "Écrire le code directement",
      "Acheter un abonnement premium"
    ],
    correctAnswer: 1,
    explanation: "Avant tout, il faut documenter le processus manuel étape par étape. C'est la base pour créer une automatisation efficace."
  },
  {
    question: "Qu'est-ce qu'un 'déclencheur' (trigger) dans une automatisation ?",
    options: [
      "L'action finale de l'automatisation",
      "L'événement qui lance le processus automatique",
      "Le bouton pour démarrer manuellement",
      "L'erreur qui arrête le processus"
    ],
    correctAnswer: 1,
    explanation: "Le déclencheur (trigger) est l'événement qui lance automatiquement le processus : réception d'email, heure précise, nouveau client, etc."
  },
  {
    question: "Quelle automatisation est recommandée pour les freelances ?",
    options: [
      "Relance automatique des devis non répondus",
      "Trading automatisé de cryptomonnaies",
      "Publication automatique sur tous les réseaux",
      "Réponse automatique à tous les emails"
    ],
    correctAnswer: 0,
    explanation: "La relance automatique des devis après 3 jours est une automatisation à fort ROI pour les freelances - elle augmente le taux de conversion sans effort."
  }
];

// Variables pour le Playground d'automatisation
const playgroundVariables = [
  {
    name: 'task_description',
    label: 'Décrivez votre tâche répétitive',
    type: 'textarea',
    placeholder: 'Ex: Chaque lundi, je compile les données de vente de la semaine depuis 3 fichiers Excel différents, je calcule les totaux et je crée un rapport PDF que j\'envoie à mon manager...'
  },
  {
    name: 'frequency',
    label: 'À quelle fréquence ?',
    type: 'select',
    options: ['Plusieurs fois par jour', 'Quotidien', 'Hebdomadaire', 'Mensuel', 'À chaque nouvel événement']
  },
  {
    name: 'time_spent',
    label: 'Temps passé à chaque fois',
    type: 'select',
    options: ['5-10 minutes', '15-30 minutes', '30-60 minutes', '1-2 heures', 'Plus de 2 heures']
  },
  {
    name: 'tools_used',
    label: 'Outils utilisés actuellement',
    type: 'text',
    placeholder: 'Ex: Excel, Gmail, Google Drive, Slack...'
  }
];

const playgroundTemplate = `Je fais cette tâche répétitive :

{{task_description}}

📅 Fréquence : {{frequency}}
⏱️ Temps passé : {{time_spent}}
🛠️ Outils actuels : {{tools_used}}

Analyse cette tâche et propose-moi :

1. **Diagnostic d'automatisation**
   - Cette tâche respecte-t-elle la règle des 3R ?
   - Quels aspects sont automatisables vs nécessitent intervention humaine ?

2. **Solution recommandée**
   - Outil(s) à utiliser (Zapier, Make, script Python, etc.)
   - Architecture de l'automatisation (déclencheur → actions)

3. **Guide de mise en place étape par étape**
   - Configuration détaillée
   - Temps estimé de mise en place
   - Tests à effectuer

4. **ROI estimé**
   - Temps gagné par semaine/mois
   - Réduction d'erreurs humaines

Donne-moi une solution pratique que je peux mettre en place aujourd'hui.`;

// Guide étape par étape pour créer sa première automatisation Zapier
const automationGuideSteps = [
  {
    title: "Créer un compte Zapier gratuit",
    content: "Rendez-vous sur zapier.com et créez un compte gratuit. Le plan gratuit permet 5 'Zaps' et 100 tâches/mois - parfait pour commencer.",
    tips: ["Utilisez un email professionnel pour accéder à plus d'intégrations", "Le plan gratuit suffit pour tester vos premières automatisations"]
  },
  {
    title: "Identifier votre premier Zap",
    content: "Choisissez une automatisation simple mais utile. Exemple parfait pour débuter : sauvegarder automatiquement les pièces jointes Gmail dans Google Drive.",
    prompt: "Voici mon workflow actuel : [décrivez-le]. Propose-moi le Zap le plus simple et utile pour commencer."
  },
  {
    title: "Configurer le déclencheur (Trigger)",
    content: "Dans Zapier, cliquez sur 'Create Zap'. Choisissez l'application déclencheur (ex: Gmail) et l'événement (ex: 'New Attachment'). Connectez votre compte Gmail.",
    code: `Exemple de configuration :
App: Gmail
Trigger: New Attachment
Account: votre-email@gmail.com
Search: label:inbox has:attachment`,
    checkpoint: "Testez le trigger - Zapier doit trouver un email récent avec pièce jointe"
  },
  {
    title: "Configurer l'action",
    content: "Ajoutez l'action : cliquez sur '+', choisissez Google Drive, action 'Upload File'. Mappez le fichier attaché au dossier de destination.",
    code: `Configuration Action :
App: Google Drive
Action: Upload File
Folder: /Pièces jointes Gmail/
File: {{attachment}}
File Name: {{attachment_name}}`,
    tips: ["Créez un dossier dédié dans Drive avant", "Utilisez des sous-dossiers par date si besoin"]
  },
  {
    title: "Tester et activer",
    content: "Cliquez sur 'Test step' pour chaque étape, puis 'Publish' pour activer votre Zap. Envoyez-vous un email test avec pièce jointe pour vérifier.",
    checkpoint: "Votre premier Zap est actif ! Vérifiez que le fichier apparaît dans Drive dans les 15 minutes."
  }
];

const Module4 = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const totalProgress = progress?.totalProgress || 0;

  return (
    <div className="dashboard-layout">
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">
            {user?.firstName?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre PRO</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">MODULES</div>
          <div className="nav-item" onClick={() => navigate('/module/1')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></span>
            <span className="nav-text">1. Introduction IA</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/2')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></span>
            <span className="nav-text">2. ChatGPT</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/3')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
            <span className="nav-text">3. Claude Code</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/module/4')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/></svg></span>
            <span className="nav-text">4. Automatisation</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/5')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg></span>
            <span className="nav-text">5. Créer des Apps</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/module/6')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">6. Projets Avancés</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour aux programmes</button>
          <div className="module-badge">MODULE 4</div>
          <h1>⚙️ Automatiser ce qui vous fait perdre du temps</h1>
          <p className="module-subtitle">Tâches répétitives, process, outils internes — tout devient automatique</p>
          <ProgressBar moduleId={MODULE_ID} />
        </div>

        <div className="module-content">

          {/* Section 1: Introduction */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>Introduction : Libérez votre temps</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="intro" />
            </div>

            <p>
              Combien d'heures par semaine passez-vous sur des tâches répétitives ?
              Envoyer des emails, copier des données, créer des rapports...
              <strong> L'IA peut faire tout ça à votre place, automatiquement.</strong>
            </p>

            <div className="highlight-box">
              <strong>🎯 Objectif du module :</strong> Identifier et automatiser les tâches répétitives
              pour gagner des heures chaque semaine et vous concentrer sur ce qui compte vraiment.
            </div>

            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">20h</div>
                <div className="stat-label">Économisées par mois en moyenne</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">80%</div>
                <div className="stat-label">Des tâches admin automatisables</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">0</div>
                <div className="stat-label">Ligne de code requise</div>
              </div>
            </div>
          </section>

          {/* Section 2: Identifier les tâches */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>1. Identifier ce qui peut être automatisé</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="identify" />
            </div>

            <h3>La règle des 3R</h3>
            <p>Une tâche est automatisable si elle respecte ces 3 critères :</p>

            <div className="principles-grid">
              <div className="principle-card">
                <div className="principle-icon">🔄</div>
                <h4>Répétitive</h4>
                <p>Vous la faites régulièrement (quotidien, hebdomadaire, mensuel)</p>
              </div>
              <div className="principle-card">
                <div className="principle-icon">📏</div>
                <h4>Régulière</h4>
                <p>Elle suit toujours le même processus, les mêmes étapes</p>
              </div>
              <div className="principle-card">
                <div className="principle-icon">🤖</div>
                <h4>Robotisable</h4>
                <p>Elle ne nécessite pas de jugement humain complexe</p>
              </div>
            </div>

            <h3>Auto-diagnostic : Ma tâche est-elle automatisable ?</h3>
            <PromptBox prompt={`Analyse cette tâche et dis-moi si elle est automatisable :

Tâche : [DÉCRIVEZ VOTRE TÂCHE]

Réponds selon ces critères :
1. Est-elle RÉPÉTITIVE ? (fréquence)
2. Est-elle RÉGULIÈRE ? (même processus à chaque fois)
3. Est-elle ROBOTISABLE ? (pas de jugement humain complexe)

Score d'automatisation : X/3
Recommandation : [AUTOMATISABLE / PARTIELLEMENT / NON AUTOMATISABLE]
Si automatisable, suggère l'outil idéal.`} title="Prompt d'analyse d'automatisation" />

            <h3>Exemples de tâches automatisables</h3>
            <div className="cards-grid">
              <div className="info-card">
                <div className="card-icon">📧</div>
                <h4>Emails</h4>
                <p>Réponses automatiques, suivis, relances clients, tris</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📊</div>
                <h4>Rapports</h4>
                <p>Génération hebdo, dashboards, compilations de données</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📱</div>
                <h4>Réseaux sociaux</h4>
                <p>Publication programmée, curation, analytics</p>
              </div>
              <div className="info-card">
                <div className="card-icon">💾</div>
                <h4>Données</h4>
                <p>Sync, nettoyage, transfert entre outils, backups</p>
              </div>
              <div className="info-card">
                <div className="card-icon">📋</div>
                <h4>Administration</h4>
                <p>Facturation, relances paiement, gestion fichiers</p>
              </div>
              <div className="info-card">
                <div className="card-icon">🔔</div>
                <h4>Notifications</h4>
                <p>Alertes, rappels, suivis automatiques, deadlines</p>
              </div>
            </div>
          </section>

          {/* Section 3: Les outils */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>2. Les outils d'automatisation</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="tools" />
            </div>

            <div className="two-columns">
              <div>
                <h3>🔗 Zapier — Le couteau suisse</h3>
                <ul className="module-list check">
                  <li>+5000 applications connectables</li>
                  <li>Interface visuelle sans code</li>
                  <li>Plan gratuit pour débuter</li>
                  <li>Idéal pour les automatisations simples</li>
                </ul>
              </div>
              <div>
                <h3>⚡ Make (ex-Integromat) — Plus puissant</h3>
                <ul className="module-list check">
                  <li>Logique conditionnelle avancée</li>
                  <li>Boucles et itérations</li>
                  <li>Moins cher pour gros volumes</li>
                  <li>Idéal pour workflows complexes</li>
                </ul>
              </div>
            </div>

            <h3>🤖 Claude + Scripts — Sur mesure</h3>
            <p>Pour les automatisations personnalisées, demandez à Claude de créer des scripts adaptés :</p>

            <PromptBox prompt={`Crée un script Python qui automatise cette tâche :
[DÉCRIVEZ VOTRE TÂCHE]

Le script doit :
- Être simple à exécuter
- Inclure des commentaires explicatifs
- Gérer les erreurs proprement
- Pouvoir être planifié (cron/task scheduler)

Donne-moi aussi les instructions d'installation et d'utilisation.`} title="Prompt pour créer un script d'automatisation" />

            <h3>Comparatif rapide</h3>
            <div className="info-box">
              <table style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr style={{borderBottom: '1px solid rgba(212, 175, 55, 0.3)'}}>
                    <th style={{textAlign: 'left', padding: '10px'}}>Outil</th>
                    <th style={{textAlign: 'left', padding: '10px'}}>Meilleur pour</th>
                    <th style={{textAlign: 'left', padding: '10px'}}>Niveau</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{padding: '10px'}}>Zapier</td>
                    <td style={{padding: '10px'}}>Débuter, connexions simples</td>
                    <td style={{padding: '10px'}}>Débutant</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>Make</td>
                    <td style={{padding: '10px'}}>Workflows complexes</td>
                    <td style={{padding: '10px'}}>Intermédiaire</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>n8n</td>
                    <td style={{padding: '10px'}}>Auto-hébergement, gratuit</td>
                    <td style={{padding: '10px'}}>Avancé</td>
                  </tr>
                  <tr>
                    <td style={{padding: '10px'}}>Scripts Python</td>
                    <td style={{padding: '10px'}}>100% personnalisé</td>
                    <td style={{padding: '10px'}}>Avancé</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Section 4: Templates d'automatisation */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>3. Automatisations prêtes à l'emploi</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="templates" />
            </div>

            <p>Voici des automatisations testées et approuvées que vous pouvez mettre en place immédiatement :</p>

            <h3>📧 Auto-réponse intelligente aux emails</h3>
            <PromptBox prompt={`Crée un système d'auto-réponse intelligent pour mes emails :

CONTEXTE :
- Je suis [VOTRE MÉTIER]
- Je reçois environ [X] emails par jour
- Types d'emails fréquents : [demandes clients, factures, newsletters...]

LE SYSTÈME DOIT :
1. Catégoriser chaque email (urgent, normal, spam, newsletter)
2. Pour les emails urgents → notification immédiate
3. Pour les demandes courantes → proposition de réponse
4. Pour les newsletters → archivage automatique

Propose l'architecture complète avec Zapier ou Make.`} title="Template : Auto-réponse email" />

            <h3>📊 Rapport hebdomadaire automatique</h3>
            <PromptBox prompt={`Crée une automatisation pour générer un rapport hebdomadaire :

DONNÉES À INCLURE :
- [Source 1 : ex. données de vente depuis Google Sheets]
- [Source 2 : ex. trafic depuis Google Analytics]
- [Source 3 : ex. tickets depuis Zendesk]

FORMAT SOUHAITÉ :
- Résumé exécutif (5 lignes max)
- KPIs clés avec évolution vs semaine précédente
- Top 3 des points d'attention
- Graphique de tendance

LIVRAISON :
- Chaque vendredi à 17h
- Par email à [destinataires]
- Copie PDF dans Google Drive

Donne-moi le workflow Make/Zapier complet.`} title="Template : Rapport automatique" />

            <h3>🔄 Onboarding client automatisé</h3>
            <PromptBox prompt={`Crée un workflow d'onboarding client automatisé :

DÉCLENCHEUR : Nouveau paiement reçu sur Stripe

ACTIONS À AUTOMATISER :
1. Créer le client dans mon CRM (HubSpot/Notion)
2. Créer un dossier client dans Google Drive
3. Envoyer l'email de bienvenue personnalisé
4. Planifier le premier call dans Calendly
5. Créer les tâches de suivi dans mon todo

PERSONNALISATION :
- Inclure le nom du client
- Adapter selon le produit/service acheté
- Ajouter rappel J+7 si pas de réponse

Configure ce Zap étape par étape.`} title="Template : Onboarding client" />
          </section>

          {/* Section 5: Créer avec l'IA */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>4. Créer des automatisations avec l'IA</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="create" />
            </div>

            <h3>La méthode DIDA en 4 étapes</h3>
            <div className="steps-list">
              <div className="step-item">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>Documenter</strong>
                  <p>Écrivez chaque étape de votre tâche manuelle actuelle</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>Identifier le déclencheur</strong>
                  <p>Qu'est-ce qui lance le processus ? Email, heure, événement...</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">3</div>
                <div className="step-content">
                  <strong>Définir les actions</strong>
                  <p>Listez ce qui doit se passer à chaque étape</p>
                </div>
              </div>
              <div className="step-item">
                <div className="step-number">4</div>
                <div className="step-content">
                  <strong>Automatiser</strong>
                  <p>Demandez à Claude de créer le workflow ou script</p>
                </div>
              </div>
            </div>

            <h3>Le prompt DIDA universel</h3>
            <PromptBox prompt={`J'ai besoin d'automatiser cette tâche :

📝 DOCUMENTATION DU PROCESSUS ACTUEL :
[Décrivez étape par étape ce que vous faites manuellement]

⚡ DÉCLENCHEUR :
[Ce qui lance le processus : réception d'email, heure précise, nouveau client, etc.]

🎯 ACTIONS À AUTOMATISER :
[Listez chaque action qui doit se produire]

🛠️ OUTILS DISPONIBLES :
[Les outils que vous utilisez déjà : Gmail, Slack, Notion, etc.]

Crée l'automatisation complète avec :
1. L'architecture du workflow (trigger → actions)
2. L'outil recommandé (Zapier, Make, ou script)
3. Guide de configuration étape par étape
4. Tests à effectuer pour valider`} title="Prompt DIDA universel" />
          </section>

          {/* Section 6: Guide pratique Zapier */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>5. Atelier : Créez votre premier Zap</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="guide" />
            </div>

            <p>Suivez ce guide pas à pas pour créer votre première automatisation fonctionnelle :</p>

            <StepByStepGuide
              moduleId={MODULE_ID}
              guideId="first-zap"
              title="Créer votre premier Zap : Gmail → Google Drive"
              steps={automationGuideSteps}
            />
          </section>

          {/* Section 7: Playground */}
          <section className="module-section">
            <div className="section-header-with-checkbox">
              <h2>6. Playground : Automatisez votre tâche</h2>
              <SectionCheckbox moduleId={MODULE_ID} sectionId="playground" />
            </div>

            <p>Décrivez une de vos tâches répétitives et obtenez une solution d'automatisation personnalisée :</p>

            <PromptPlayground
              variables={playgroundVariables}
              template={playgroundTemplate}
              title="Générateur d'automatisation personnalisée"
            />

            <div className="tip-box">
              <div className="tip-icon">💡</div>
              <div className="tip-content">
                <h4>Conseil pro</h4>
                <p>Commencez par automatiser une seule tâche simple. Une fois maîtrisée,
                   vous pourrez créer des chaînes d'automatisation plus complexes.
                   Le ROI d'une bonne automatisation se mesure en heures gagnées par semaine !</p>
              </div>
            </div>
          </section>

          {/* Quiz */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur l'automatisation. Score minimum : 70%</p>
            <Quiz
              moduleId={MODULE_ID}
              questions={quizQuestions}
            />
          </section>

          {/* Résumé */}
          <section className="module-section">
            <h2>📋 Résumé du module</h2>
            <div className="summary-box">
              <ul>
                <li><strong>Règle des 3R</strong> — Répétitif + Régulier + Robotisable = Automatisable</li>
                <li><strong>Outils clés</strong> — Zapier (simple), Make (avancé), Scripts (sur mesure)</li>
                <li><strong>Méthode DIDA</strong> — Documenter, Identifier, Définir, Automatiser</li>
                <li><strong>Commencez simple</strong> — Une automatisation à la fois, testez bien</li>
                <li><strong>ROI rapide</strong> — Chaque heure automatisée = temps récupéré à vie</li>
              </ul>
            </div>
          </section>

          {/* Discord */}
          <DiscordWidget />

          {/* Navigation */}
          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/module/3')}>← Module précédent</button>
            <button className="btn btn-primary" onClick={() => navigate('/module/5')}>Module suivant : Créer des Apps →</button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Module4;
